import {
    type ScreenTokens,
    type PlantDevice,
    FOCUS_WIDTH
} from "./model";

export const UI_SCALE = 0.3;

export const SCREEN_SIZES: Record<PlantDevice, {
    height: number;
    width: number;
}> = {
    monitor: {
        height: 240,
        width: 387
    },
    phone: {
        height: 327,
        width: 147
    }
};

const LINE = 1.5;

const fixed = (value: number) => Math.round(value * 100) / 100;

const box = (x: number, y: number, width: number, height: number, radius: number, fill: string, stroke = "") => `<rect x="${fixed(x)}" y="${fixed(y)}" width="${fixed(width)}" height="${fixed(height)}" rx="${fixed(Math.min(radius, height / 2, width / 2))}" fill="${fill}"${stroke ? ` stroke="${stroke}" stroke-width="${LINE}"` : ""}/>`;

const rule = (x: number, y: number, width: number, height: number, fill: string) => box(x, y, width, height, height / 2, fill);

const dot = (x: number, y: number, radius: number, fill: string) => `<circle cx="${fixed(x)}" cy="${fixed(y)}" r="${fixed(radius)}" fill="${fill}"/>`;

const FOCUS_GAP = 2;

const focusRing = (x: number, y: number, width: number, height: number, radius: number, color: string) => {
    const reach = FOCUS_GAP + FOCUS_WIDTH / 2;

    return `<rect x="${fixed(x - reach)}" y="${fixed(y - reach)}" width="${fixed(width + reach * 2)}" height="${fixed(height + reach * 2)}" rx="${fixed(Math.min(radius, height / 2) + reach)}" fill="none" stroke="${color}" stroke-width="${FOCUS_WIDTH}"/>`;
};

const cursorAt = (x: number, y: number, fill: string, outline: string) => `<path d="M${fixed(x)} ${fixed(y)}l0 15l4.2 -3.8l3.1 6.6l2.6 -1.2l-3.1 -6.5l5.6 -0.3z" fill="${fill}" stroke="${outline}" stroke-width="1.2" stroke-linejoin="round"/>`;

type CardState = {
    isSecondaryHovered: boolean;
    isPrimaryPressed: boolean;
    isInputFocused: boolean;
    hasSecondary: boolean;
};

const SWITCH_WIDTH = 36;

const SWITCH_HEIGHT = 20;

/**
 * One card the way the kit lays it out: a title, an input, the actions and a switch row.
 * Returns the markup and the card's height, so the layouts around it can follow the density.
 */
const cardOf = (x: number, y: number, width: number, tokens: ScreenTokens, cardState: CardState) => {
    const {
        controlPadding,
        controlHeight,
        cardPadding,
        colors,
        gap
    } = tokens;

    const inner = width - cardPadding * 2;

    const left = x + cardPadding;

    const titleY = y + cardPadding;

    const inputY = titleY + 22;

    const actionsY = inputY + controlHeight + gap + 4;

    const switchY = actionsY + controlHeight + gap + 6;

    const height = switchY + SWITCH_HEIGHT + cardPadding - y;

    const {
        isSecondaryHovered,
        isPrimaryPressed,
        isInputFocused,
        hasSecondary
    } = cardState;

    const primaryWidth = hasSecondary ? Math.min(inner * 0.5, controlPadding * 2 + 48) : inner;

    const secondaryX = left + primaryWidth + 8;

    const secondaryWidth = controlPadding * 2 + 36;

    const secondary = hasSecondary ? box(secondaryX, actionsY, secondaryWidth, controlHeight, tokens.actions, colors.raised, colors.border) + (isSecondaryHovered ? box(secondaryX, actionsY, secondaryWidth, controlHeight, tokens.actions, colors.hover) : "") + rule(secondaryX + controlPadding, actionsY + controlHeight / 2 - 2, 36, 4, colors.textHigh) : "";

    const touch = isPrimaryPressed ? `<circle class="plant-touch" cx="${fixed(left + primaryWidth * 0.68)}" cy="${fixed(actionsY + controlHeight / 2)}" r="${fixed(controlHeight * 0.42)}" fill="${colors.onPrimary}"/>` : "";

    const pointer = isSecondaryHovered ? cursorAt(secondaryX + secondaryWidth * 0.62, actionsY + controlHeight * 0.45, colors.textHigh, colors.page) : "";

    const markup = [
        box(x, y, width, height, tokens.surface, colors.raised, colors.borderSubtle),
        rule(left, titleY, inner * 0.56, 6, colors.textHigh),
        rule(left, titleY + 11, inner * 0.82, 4, colors.textLow),
        box(left, inputY, inner, controlHeight, tokens.form, colors.page, colors.border),
        isInputFocused ? focusRing(left, inputY, inner, controlHeight, tokens.form, colors.focus) : "",
        isInputFocused ? `<rect class="plant-caret" x="${fixed(left + controlPadding)}" y="${fixed(inputY + controlHeight * 0.27)}" width="1.6" height="${fixed(controlHeight * 0.46)}" fill="${colors.textHigh}"/>` : "",
        rule(left + controlPadding + (isInputFocused ? 6 : 0), inputY + controlHeight / 2 - 2, inner * 0.42, 4, colors.textLow),
        box(left, actionsY, primaryWidth, controlHeight, tokens.actions, colors.primary),
        rule(left + primaryWidth / 2 - 18, actionsY + controlHeight / 2 - 2, 36, 4, colors.onPrimary),
        touch,
        secondary,
        rule(left, switchY + SWITCH_HEIGHT / 2 - 2.5, inner * 0.38, 5, colors.textHigh),
        box(left + inner - SWITCH_WIDTH, switchY, SWITCH_WIDTH, SWITCH_HEIGHT, SWITCH_HEIGHT / 2, colors.primary),
        dot(left + inner - SWITCH_HEIGHT / 2, switchY + SWITCH_HEIGHT / 2, SWITCH_HEIGHT / 2 - 3, colors.onPrimary),
        pointer
    ].join("");

    return {
        markup,
        height
    };
};

const chipsOf = (x: number, y: number, widths: Array<number>, tokens: ScreenTokens) => {
    const height = tokens.smallHeight - 8;

    let cursor = x;

    return widths.map((width, index) => {
        const chipX = cursor;

        cursor += width + 6;

        return box(chipX, y, width, height, tokens.chip, index === 0 ? tokens.colors.subtle : tokens.colors.page, tokens.colors.borderSubtle) + rule(chipX + 9, y + height / 2 - 2, width - 18, 4, tokens.colors.textHigh);
    }).join("");
};

const phoneOf = (tokens: ScreenTokens) => {
    const {
        height,
        width
    } = SCREEN_SIZES.phone;

    const {
        colors
    } = tokens;

    const card = cardOf(10, 52, width - 20, tokens, {
        isSecondaryHovered: false,
        isPrimaryPressed: true,
        isInputFocused: false,
        hasSecondary: false
    });

    const chipsY = 52 + card.height + 14;

    const barY = height - 30;

    return [
        box(0, 0, width, height, 0, colors.page),
        rule(14, 11, 22, 5, colors.textHigh),
        box(width - 34, 10, 20, 7, 2, colors.textHigh),
        rule(10, 32, 72, 8, colors.textHigh),
        card.markup,
        chipsOf(10, chipsY, [
            44,
            50,
            32
        ], tokens),
        box(0, barY, width, 30, 0, colors.raised),
        box(0, barY, width, LINE, 0, colors.borderSubtle),
        [
            0,
            1,
            2,
            3
        ].map((index) => dot(22 + index * ((width - 44) / 3), barY + 15, 4.5, index === 0 ? colors.primary : colors.textLow)).join("")
    ].join("");
};

const monitorOf = (tokens: ScreenTokens) => {
    const {
        height,
        width
    } = SCREEN_SIZES.monitor;

    const {
        colors
    } = tokens;

    const card = cardOf(16, 44, 206, tokens, {
        isSecondaryHovered: true,
        isPrimaryPressed: false,
        isInputFocused: true,
        hasSecondary: true
    });

    const sideX = 236;

    const sideWidth = width - sideX - 16;

    const listY = 44 + 12 + tokens.smallHeight;

    const rows = [
        0,
        1,
        2
    ].map((index) => {
        const rowY = listY + 14 + index * 30;

        return dot(sideX + 22, rowY + 8, 8, index === 0 ? colors.primary : colors.subtle) + rule(sideX + 38, rowY + 2, sideWidth - 64, 5, colors.textHigh) + rule(sideX + 38, rowY + 11, sideWidth - 84, 4, colors.textLow);
    }).join("");

    return [
        box(0, 0, width, height, 0, colors.page),
        box(0, 0, width, 28, 0, colors.raised),
        box(0, 28, width, LINE, 0, colors.borderSubtle),
        box(14, 8, 12, 12, 3, colors.primary),
        rule(36, 12, 30, 4, colors.textHigh),
        rule(74, 12, 26, 4, colors.textLow),
        rule(108, 12, 30, 4, colors.textLow),
        dot(width - 22, 14, 7, colors.subtle),
        card.markup,
        chipsOf(sideX, 44, [
            40,
            46,
            34
        ], tokens),
        box(sideX, listY, sideWidth, Math.max(card.height - (listY - 44), 104), tokens.surface, colors.raised, colors.borderSubtle),
        rows
    ].join("");
};

export const screenMarkupOf = (device: PlantDevice, tokens: ScreenTokens) => device === "phone" ? phoneOf(tokens) : monitorOf(tokens);
